import React, { useState, useEffect } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import inventoryService from '../../services/inventoryService';

const TransferFormModal = ({ stores, products = [], activeStoreId, onSave, onCancel, error }) => {
  const [fromStore, setFromStore] = useState(activeStoreId || '');
  const [toStore, setToStore] = useState('');
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (!fromStore || !searchTerm.trim()) {
      setSearchResults([]);
      setSearchError('');
      setIsSearching(false);
      return;
    }

    const debounce = setTimeout(async () => {
      try {
        setIsSearching(true);
        setSearchError('');
        const results = await inventoryService.searchProducts(fromStore, searchTerm.trim());
        setSearchResults(Array.isArray(results) ? results : []);
      } catch (err) {
        setSearchError('Failed to search products.');
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 250);

    return () => clearTimeout(debounce);
  }, [fromStore, searchTerm]);
  const handleFromStoreChange = (storeId) => {
    setFromStore(storeId);
    setItems([]);
    setSearchTerm('');
    setSelectedProduct(null);
    setSearchResults([]);
    setSearchError('');
    if (storeId === toStore) {
      setToStore('');
    }
  };


  const handleAddItem = () => {
    if (!selectedProduct || quantity <= 0) return;
    if (!items.some(i => i.product === selectedProduct._id)) {
      setItems([...items, { product: selectedProduct._id, name: selectedProduct.name, quantity }]);
      setSelectedProduct(null);
      setSearchTerm('');
      setQuantity(1);
      setSearchResults([]);
    }
  };

  const handleRemoveItem = (productId) => {
    setItems(items.filter(i => i.product !== productId));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (items.length === 0 || !fromStore || !toStore || fromStore === toStore) return;
    // --- THE FIX IS HERE: Use 'quantityShipped' to match the new schema ---
    // This is a structural change, not a variable rename.
    const itemsToSave = items.map(item => ({
        product: item.product,
        name: item.name,
        quantity: item.quantity // The backend expects 'quantity' on creation
    }));
    onSave({ fromStore, toStore, items: itemsToSave, notes });
    // --- END OF FIX ---
  };

  const isFormInvalid = items.length === 0 || !fromStore || !toStore || fromStore === toStore;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-2xl max-h-[calc(100vh-2rem)] overflow-y-auto">
        <h2 className="text-2xl font-bold mb-4">Create New Stock Transfer</h2>
        {error && <p className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">From Store</label>
              <select
                value={fromStore}
                onChange={(e) => handleFromStoreChange(e.target.value)}
                required
                className="mt-1 p-2 border rounded-md w-full"
              >
                <option value="">Select source...</option>
                {stores.map((store) => (
                  <option key={store._id} value={store._id}>{store.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">To Store</label>
              <select value={toStore} onChange={(e) => setToStore(e.target.value)} required className="mt-1 p-2 border rounded-md w-full">
                <option value="">Select destination...</option>
                {stores.filter(s => s._id !== fromStore).map(s => (
                  <option key={s._id} value={s._id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-lg font-semibold mb-2">Items to Transfer</h3>
            <div className="flex items-end gap-2 mb-4">
              <div className="flex-grow">
                <label className="block text-sm font-medium text-gray-700">Product</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setSelectedProduct(null);
                    }}
                    placeholder="Search product name, SKU, or barcode..."
                    className="mt-1 p-2 border rounded-md w-full"
                  />
                  {isSearching && (
                    <span className="absolute right-3 top-3 text-xs text-gray-400">Searching…</span>
                  )}
                </div>
                {searchError && <p className="text-xs text-red-600 mt-1">{searchError}</p>}
                {searchResults.length > 0 && !selectedProduct && (
                  <div className="mt-2 max-h-48 overflow-y-auto border rounded-md bg-white shadow">
                    {searchResults.map((product) => (
                      <button
                        type="button"
                        key={product._id}
                        onClick={() => {
                          setSelectedProduct(product);
                          setSearchTerm(product.name);
                          setSearchResults([]);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0"
                      >
                        <div className="text-sm font-semibold text-slate-900">{product.name}</div>
                        <div className="text-xs text-slate-500">
                          SKU: {product.sku || '—'} • Stock: {product.stock ?? 0}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {!searchTerm && products.length > 0 && (
                  <p className="text-xs text-slate-400 mt-1">
                    Start typing to search products. ({products.length} products loaded)
                  </p>
                )}
                {selectedProduct && (
                  <div className="mt-2 text-xs text-slate-500">
                    Selected: <span className="font-semibold text-slate-700">{selectedProduct.name}</span>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Quantity</label>
                <input type="number" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value))} min="1" className="mt-1 p-2 border rounded-md w-24" />
              </div>
              <button type="button" onClick={handleAddItem} aria-label="Add item" className="p-2 bg-primary text-white rounded-md hover:bg-primary-dark"><FiPlus /></button>
            </div>
            <div className="max-h-48 overflow-y-auto border rounded-md">
              {items.map(item => (
                <div key={item.product} className="flex justify-between items-center p-2 border-b">
                  <span>{item.name} x {item.quantity}</span>
                  <button type="button" onClick={() => handleRemoveItem(item.product)} className="text-red-500"><FiTrash2 /></button>
                </div>
              ))}
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows="2" className="mt-1 p-2 border rounded-md w-full"></textarea>
          </div>

          <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
            <button type="button" onClick={onCancel} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md">Cancel</button>
            <button 
              type="submit" 
              disabled={isFormInvalid}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Create Transfer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TransferFormModal;
